import { buildEsConditionSchema } from "@/components/ConditionPageLayoutEs";
import type { Crumb } from "@/components/schemaConstants";
import { SITE_URL, buildHreflang } from "@/lib/i18n/locale";

/** Shared brain image for Spanish ketamine condition pages. */
export const BRAIN_IMG =
  "https://vibe.filesafe.space/1787327131284421342/assets/27046b62-cf92-4390-833e-730db34817fa0.png";

/** E-E-A-T: named medical reviewer + ISO date for the Spanish condition pages. */
export const ES_REVIEWED_BY = "Daniel Pietras, MD";
export const ES_LAST_UPDATED = "2026-09-15";

/** Build the Spanish Services > Afecciones breadcrumb trail. */
export const esBaseCrumbs = (leafName: string, leafHref: string): Crumb[] => [
  { name: "Servicios", href: "/es/services-directory" },
  {
    name: "Afecciones tratadas",
    href: "/es/ketamine-treatment-coral-springs-fl",
  },
  { name: leafName, href: leafHref },
];

/** Strip the /es/ locale prefix to recover the equivalent English path. */
const stripEs = (path: string) => path.replace(/^\/es\//, "/");

/**
 * Build the route `head()` for a Spanish ketamine condition page from its
 * data config: title/description/og, self-canonical, bidirectional hreflang
 * alternates (en / es / x-default), and the MedicalWebPage + BreadcrumbList
 * JSON-LD server-rendered into <head> scripts.
 */
export function buildEsConditionHead(cfg: {
  canonical: string;
  seoTitle: string;
  seoDescription: string;
  keywords?: string;
  breadcrumbs?: Crumb[];
  extraSchema?: object | object[];
}) {
  const url = `${SITE_URL}${cfg.canonical}`;
  return {
    meta: [
      { title: cfg.seoTitle },
      { name: "description", content: cfg.seoDescription },
      ...(cfg.keywords ? [{ name: "keywords", content: cfg.keywords }] : []),
      { property: "og:title", content: cfg.seoTitle },
      { property: "og:description", content: cfg.seoDescription },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "es_ES" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: cfg.seoTitle },
      { name: "twitter:description", content: cfg.seoDescription },
    ],
    links: [
      { rel: "canonical", href: url },
      ...buildHreflang(stripEs(cfg.canonical)),
    ],
    scripts: [
      {
        type: "application/ld+json",
        content: JSON.stringify(
          buildEsConditionSchema({
            seoTitle: cfg.seoTitle,
            seoDescription: cfg.seoDescription,
            canonical: cfg.canonical,
            breadcrumbs: cfg.breadcrumbs,
            extraSchema: cfg.extraSchema,
          }),
        ),
      },
    ],
  };
}
